/**
 * GatherReadinessSection
 * 
 * Section A of the Gather phase UI - Readiness (Setup)
 * 
 * This section produces prerequisites that must be satisfied before
 * execution work (sessions/tools) can begin. It is the canonical
 * location for:
 * 
 * A1. Criteria Set Readiness - selecting and locking criteria
 * A2. Per-Criterion Scale Readiness - defining value scales for each criterion
 * 
 * Per canonical constraints:
 * - DB is the system of record; no UI-only readiness state
 * - No optimistic navigation; writes must complete before enabling next actions
 * - All readiness state is persisted and queryable
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { AuthorizationGate } from '../AuthorizationGate';
import {
  CheckCircle2,
  Circle,
  Lock,
  Unlock,
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Calendar,
  X,
} from 'lucide-react';
import { useCycleComparisonSetStore } from '../../../stores/prioritization/cycleComparisonSetStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useAttributeGovernanceStore, type ValueRange } from '../../../stores/prioritization/attributeGovernanceStore';
import type { CycleProgress } from '../../../stores/prioritization/cycleProgressStore';

// ============================================================================
// Scale Definition Modal
// ============================================================================

const SCALE_PRESETS: { id: string; label: string; range: ValueRange }[] = [
  {
    id: 'hml',
    label: 'High / Medium / Low',
    range: { type: 'select', options: ['High', 'Medium', 'Low'] },
  },
  {
    id: 'scale5',
    label: '1-5 Scale',
    range: { type: 'scale', min: 1, max: 5 },
  },
  {
    id: 'scale10',
    label: '1-10 Scale',
    range: { type: 'scale', min: 1, max: 10 },
  },
  {
    id: 'fibonacci',
    label: 'Fibonacci (1, 2, 3, 5, 8, 13, 21)',
    range: { type: 'fibonacci', options: [1, 2, 3, 5, 8, 13, 21] },
  },
  {
    id: 'tshirt',
    label: 'T-Shirt Sizes (XS - XXL)',
    range: { type: 'select', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
  },
];

interface ScaleDefinitionModalProps {
  isOpen: boolean;
  criterionKey: string;
  criterionLabel: string;
  cycleId: string;
  /** Required: the track this config belongs to (cycle_attribute_configs.track_id NOT NULL). */
  trackId: string;
  userId: string;
  onClose: () => void;
  onScaleDefined: () => void;
}

function ScaleDefinitionModal({
  isOpen,
  criterionKey,
  criterionLabel,
  cycleId,
  trackId,
  userId,
  onClose,
  onScaleDefined,
}: ScaleDefinitionModalProps) {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const createCycleConfig = useAttributeGovernanceStore(state => state.createCycleConfig);
  const updateCycleConfig = useAttributeGovernanceStore(state => state.updateCycleConfig);
  const cycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  
  const handleSave = useCallback(async () => {
    if (!selectedPreset) return;
    
    const preset = SCALE_PRESETS.find(p => p.id === selectedPreset);
    if (!preset) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Find existing config for this attribute (scoped to cycle + track)
      const existingConfig = cycleConfigs.find(
        c => c.cycleId === cycleId && c.trackId === trackId && c.attributeKey === criterionKey
      );
      
      if (existingConfig) {
        // Update existing config directly to 'accepted' status
        await updateCycleConfig(existingConfig.id, {
          rangeStatus: 'accepted',
          rangeValue: preset.range,
          rangeProposedBy: userId,
          rangeProposedAt: new Date(),
          rangeAcceptedBy: userId,
          rangeAcceptedAt: new Date(),
        });
      } else {
        // Create new cycle config directly in 'accepted' status
        await createCycleConfig({
          cycleId,
          trackId,
          attributeKey: criterionKey,
          rangeStatus: 'accepted',
          rangeValue: preset.range,
          rangeProposedBy: userId,
          rangeProposedAt: new Date(),
          rangeAcceptedBy: userId,
          rangeAcceptedAt: new Date(),
        });
      }
      
      onScaleDefined();
      onClose();
    } catch (err) {
      console.error('[ScaleDefinitionModal] Error saving scale:', err);
      setError(err instanceof Error ? err.message : 'Failed to save scale');
    } finally {
      setIsSaving(false);
    }
  }, [selectedPreset, cycleId, trackId, criterionKey, userId, cycleConfigs, createCycleConfig, updateCycleConfig, onScaleDefined, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Define Scale</h3>
            <p className="text-sm text-gray-500">For: {criterionLabel}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6 space-y-3">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <p className="text-sm text-gray-600 mb-4">
            Select a scale type for this criterion. This determines what values can be assigned.
          </p>
          
          {SCALE_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => setSelectedPreset(preset.id)}
              className={`w-full text-left px-4 py-3 border rounded-lg transition-colors ${
                selectedPreset === preset.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="font-medium">{preset.label}</span>
            </button>
          ))}
        </div>
        
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedPreset || isSaving}
            className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Scale'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

interface GatherReadinessSectionProps {
  cycleId: string;
  cycleName: string;
  userId: string;
  /** The track whose attribute configs are being managed. Required by cycle_attribute_configs.track_id NOT NULL. */
  trackId: string;
  cycleProgress: CycleProgress | null;
  onCriteriaLocked: () => void;
  onRefresh: () => void;
}

interface CriterionScaleStatus {
  key: string;
  label: string;
  scaleStatus: 'accepted' | 'proposed' | 'pending';
  scaleDefined: boolean;
  scaleLabel?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

function CriteriaSetStatus({
  isLocked,
  lockedAt,
  lockedBy: _lockedBy,
  criteriaCount,
}: {
  isLocked: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  criteriaCount: number;
}) {
  if (isLocked) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <Lock className="h-3.5 w-3.5" />
          Locked
        </span>
        {lockedAt && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(lockedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
      <Unlock className="h-3.5 w-3.5" />
      Draft ({criteriaCount} criteria)
    </span>
  );
}

function CriterionScaleRow({
  criterion,
  onDefineScale,
}: {
  criterion: CriterionScaleStatus;
  onDefineScale: (key: string) => void;
}) {
  const getStatusIcon = () => {
    if (criterion.scaleStatus === 'accepted') {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
    if (criterion.scaleStatus === 'proposed') {
      return <Circle className="h-4 w-4 text-amber-500" />;
    }
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  };

  const getStatusLabel = () => {
    if (criterion.scaleStatus === 'accepted') {
      return (
        <span className="text-green-700 text-sm">
          {criterion.scaleLabel || 'Accepted'}
        </span>
      );
    }
    if (criterion.scaleStatus === 'proposed') {
      return (
        <span className="text-amber-600 text-sm">Pending Approval</span>
      );
    }
    return (
      <button
        onClick={() => onDefineScale(criterion.key)}
        className="text-red-600 hover:text-red-700 text-sm font-medium underline flex items-center gap-1"
      >
        Define Scale
        <ExternalLink className="h-3 w-3" />
      </button>
    );
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        {getStatusIcon()}
        <span className="font-medium text-gray-900">{criterion.label}</span>
      </div>
      <div className="flex items-center gap-2">
        {getStatusLabel()}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GatherReadinessSection({
  cycleId,
  cycleName,
  userId,
  trackId,
  cycleProgress: _cycleProgress,
  onCriteriaLocked,
  onRefresh,
}: GatherReadinessSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLocking, setIsLocking] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [scaleModalCriterion, setScaleModalCriterion] = useState<{ key: string; label: string } | null>(null);
  
  // Stores
  const allComparisonSets = useCycleComparisonSetStore(state => state.sets);
  const createComparisonSet = useCycleComparisonSetStore(state => state.createSet);
  const lockCriteria = useCycleComparisonSetStore(state => state.lockCriteria);
  const updateCriteria = useCycleComparisonSetStore(state => state.updateCriteria);
  const fetchComparisonSet = useCycleComparisonSetStore(state => state.fetchForCycle);
  
  const allAttributes = useAttributesStore(state => state.attributes);
  
  const cycleConfigs = useAttributeGovernanceStore(state => state.cycleConfigs);
  const fetchCycleConfigs = useAttributeGovernanceStore(state => state.fetchCycleConfigs);
  
  // Get criteria set for this cycle (from cycle_comparison_sets table)
  const comparisonSet = useMemo(() => 
    allComparisonSets.find(s => s.cycleId === cycleId),
    [allComparisonSets, cycleId]
  );
  
  const isCriteriaLocked = comparisonSet?.lockedAt !== undefined;
  const selectedCriteriaIds = useMemo(() => comparisonSet?.attributeIds || [], [comparisonSet?.attributeIds]);
  
  // Filter to prioritization-ready attributes (for criteria selection)
  const criteriaEligibleAttributes = useMemo(() => 
    allAttributes.filter(attr => 
      attr.type === 'number' || 
      attr.type === 'scale' || 
      attr.type === 'select' ||
      attr.type === 'enum'
    ),
    [allAttributes]
  );
  
  // Build criterion scale status for each selected criterion
  const criterionScaleStatuses = useMemo((): CriterionScaleStatus[] => {
    const cycleConfigsForCycle = cycleConfigs.filter(c => c.cycleId === cycleId);
    const configMap = new Map(cycleConfigsForCycle.map(c => [c.attributeKey, c]));
    
    return selectedCriteriaIds.map(criterionKey => {
      const attr = allAttributes.find(a => a.key === criterionKey);
      const config = configMap.get(criterionKey);
      
      let scaleStatus: 'accepted' | 'proposed' | 'pending' = 'pending';
      let scaleLabel: string | undefined;
      
      if (config?.rangeStatus === 'accepted') {
        scaleStatus = 'accepted';
        if (config.rangeValue?.type) {
          scaleLabel = config.rangeValue.type === 'fibonacci' ? 'Fibonacci' :
                       config.rangeValue.type === 'tshirt' ? 'T-Shirt' :
                       config.rangeValue.type === 'scale' ? `${config.rangeValue.min}-${config.rangeValue.max}` :
                       config.rangeValue.options?.join(', ') || 'Custom';
        }
      } else if (config?.rangeStatus === 'proposed') {
        scaleStatus = 'proposed';
      }
      
      return {
        key: criterionKey,
        label: attr?.label || criterionKey,
        scaleStatus,
        scaleDefined: scaleStatus !== 'pending',
        scaleLabel,
      };
    });
  }, [selectedCriteriaIds, allAttributes, cycleConfigs, cycleId]);
  
  // Compute readiness
  const allScalesReady = criterionScaleStatuses.every(c => c.scaleStatus === 'accepted');
  const pendingScalesCount = criterionScaleStatuses.filter(c => c.scaleStatus !== 'accepted').length;
  
  // Fetch cycle configs on mount
  useEffect(() => {
    if (cycleId) {
      fetchCycleConfigs(cycleId);
    }
  }, [cycleId, fetchCycleConfigs]);
  
  // Handlers with proper await pattern (no optimistic enablement)
  const handleCreateCriteriaSet = useCallback(async () => {
    if (!cycleId || !userId || isCreating) return;
    
    setIsCreating(true);
    try {
      // 1. Perform write
      await createComparisonSet({
        cycleId,
        name: `Criteria for ${cycleName}`,
        attributeIds: [],
        createdBy: userId,
      });
      // 2-3. Re-read canonical state
      await fetchComparisonSet(cycleId);
      // 4. Notify parent to refresh progress
      onRefresh();
    } finally {
      setIsCreating(false);
    }
  }, [cycleId, userId, cycleName, isCreating, createComparisonSet, fetchComparisonSet, onRefresh]);
  
  const handleLockCriteria = useCallback(async () => {
    if (!cycleId || !userId || isLocking) return;
    
    setIsLocking(true);
    try {
      // 1. Perform write
      await lockCriteria(cycleId, userId);
      // 2-3. Re-read canonical state
      await fetchComparisonSet(cycleId);
      // 4. Notify parent
      onCriteriaLocked();
      onRefresh();
    } finally {
      setIsLocking(false);
    }
  }, [cycleId, userId, isLocking, lockCriteria, fetchComparisonSet, onCriteriaLocked, onRefresh]);
  
  const handleToggleCriterion = useCallback(async (key: string) => {
    if (!comparisonSet || isCriteriaLocked) return;
    
    const isSelected = selectedCriteriaIds.includes(key);
    const newIds = isSelected
      ? selectedCriteriaIds.filter(id => id !== key)
      : [...selectedCriteriaIds, key];
    
    // 1. Perform write
    await updateCriteria(cycleId, newIds, {}, userId);
    // 2-3. Re-read canonical state
    await fetchComparisonSet(cycleId);
    await fetchCycleConfigs(cycleId);
    // 4. Refresh progress
    onRefresh();
  }, [comparisonSet, isCriteriaLocked, selectedCriteriaIds, cycleId, userId, updateCriteria, fetchComparisonSet, fetchCycleConfigs, onRefresh]);
  
  const handleDefineScale = useCallback((criterionKey: string) => {
    // Find the criterion label
    const criterion = criterionScaleStatuses.find(c => c.key === criterionKey);
    const label = criterion?.label || criterionKey;
    
    console.log('[GatherReadiness] Opening scale definition for:', criterionKey);
    setScaleModalCriterion({ key: criterionKey, label });
  }, [criterionScaleStatuses]);
  
  const handleScaleDefined = useCallback(async () => {
    // Re-fetch governance configs to get updated scale status
    await fetchCycleConfigs(cycleId);
    onRefresh();
  }, [cycleId, fetchCycleConfigs, onRefresh]);
  
  // Compute lock button state
  const canLock = selectedCriteriaIds.length > 0 && !isCriteriaLocked;
  const lockButtonDisabled = !canLock || isLocking;
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
          <Settings className="h-5 w-5 text-indigo-500" />
          <div>
            <h3 className="font-semibold text-gray-900">Readiness (Setup)</h3>
            <p className="text-sm text-gray-500">Configure criteria and scales before execution</p>
          </div>
        </div>
        
        <CriteriaSetStatus
          isLocked={isCriteriaLocked}
          lockedAt={comparisonSet?.lockedAt}
          lockedBy={comparisonSet?.lockedBy}
          criteriaCount={selectedCriteriaIds.length}
        />
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* A1: Criteria Set Readiness */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">A1. Criteria Selection</h4>
              {comparisonSet && !isCriteriaLocked && (
                <AuthorizationGate
                  action="criteria.lock"
                  resource={{ type: 'cycle' as const, id: cycleId }}
                >
                  <button
                    onClick={handleLockCriteria}
                    disabled={lockButtonDisabled}
                    className={`btn btn-primary btn-sm flex items-center gap-1 ${
                      lockButtonDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {isLocking ? (
                      <>
                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Locking...
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4" />
                        Lock Criteria Set
                      </>
                    )}
                  </button>
                </AuthorizationGate>
              )}
              {/* Roadmap 1C (V-11): a permanently disabled "Create New Version"
                  button stood here. Versioning locked criteria is not built, so
                  the control is not drawn (build contract §2.8). */}
            </div>
            
            {!comparisonSet ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700 mb-3">
                  No criteria configured for this cycle. Initialize criteria selection to define which attributes will be used for prioritization.
                </p>
                <button
                  onClick={handleCreateCriteriaSet}
                  disabled={isCreating}
                  className="btn btn-primary btn-sm"
                >
                  {isCreating ? 'Initializing...' : 'Select Criteria'}
                </button>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                {!isCriteriaLocked ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-3">
                      Select the criteria (attributes) that will be used to evaluate items in this cycle.
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {criteriaEligibleAttributes.length === 0 ? (
                        <p className="text-sm text-gray-500 col-span-2">
                          No prioritization attributes available. Create attributes in Settings → Attributes.
                        </p>
                      ) : (
                        criteriaEligibleAttributes.map(attr => {
                          const isSelected = selectedCriteriaIds.includes(attr.key);
                          return (
                            <label 
                              key={attr.key} 
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleCriterion(attr.key)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm text-gray-900">{attr.label}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {selectedCriteriaIds.length === 0 && (
                      <p className="text-sm text-amber-600 mt-2">
                        Select at least one criterion to enable locking.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-2">
                      Criteria locked - {selectedCriteriaIds.length} criteria selected
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCriteriaIds.map(key => {
                        const attr = allAttributes.find(a => a.key === key);
                        return (
                          <span 
                            key={key}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm"
                          >
                            {attr?.label || key}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* A2: Per-Criterion Scale Readiness */}
          {selectedCriteriaIds.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">A2. Scale Readiness</h4>
                <span className={`text-sm ${allScalesReady ? 'text-green-600' : 'text-amber-600'}`}>
                  {allScalesReady 
                    ? 'All scales ready' 
                    : `${pendingScalesCount} scale${pendingScalesCount > 1 ? 's' : ''} need definition`
                  }
                </span>
              </div>
              
              <p className="text-sm text-gray-600">
                Define value scales (e.g., High/Medium/Low, 1-5) for each criterion before value entry can begin.
              </p>
              
              <div className="space-y-2">
                {criterionScaleStatuses.map(criterion => (
                  <CriterionScaleRow
                    key={criterion.key}
                    criterion={criterion}
                    onDefineScale={handleDefineScale}
                  />
                ))}
              </div>
              
              {!allScalesReady && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  <strong>Blocked:</strong> Value entry sessions cannot run until all scales are defined and accepted.
                </div>
              )}
            </div>
          )}
          
          {/* Provenance */}
          {isCriteriaLocked && comparisonSet?.lockedAt && (
            <div className="border-t border-gray-200 pt-4 text-sm text-gray-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Lock className="h-3.5 w-3.5" />
                  Locked by {comparisonSet.lockedBy || 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(comparisonSet.lockedAt).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Scale Definition Modal */}
      <ScaleDefinitionModal
        isOpen={!!scaleModalCriterion}
        criterionKey={scaleModalCriterion?.key || ''}
        criterionLabel={scaleModalCriterion?.label || ''}
        cycleId={cycleId}
        trackId={trackId}
        userId={userId}
        onClose={() => setScaleModalCriterion(null)}
        onScaleDefined={handleScaleDefined}
      />
    </div>
  );
}

export default GatherReadinessSection;
